import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, Grid, Typography } from "@material-ui/core";

import EmailAuth from "components/EmailAuth";
import LoadingSpinner from "components/LoadingSpinner";
import EmailIcon from "@material-ui/icons/Email";
import AppleIcon from "@material-ui/icons/Apple";
import googleIcon from "images/google_icon.svg";
import { performGoogleSignIn, performAppleSignIn } from "utils/firebase/auth/social";
import { useProcessSocialSignInResponse, useSignOutUser } from "hooks";
import { checkIsInIframe, requestParentToSignIn } from "utils/marketingSiteMessageUtils";

const AuthCTA = (props) => {
  const authUser = useSelector((state) => state.user.authUser);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [signUpModalOpen, setSignUpModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const signOutUser = useSignOutUser();

  const isSignUp = props.signUp || signUpModalOpen;

  const { processSocialSignInResponse } = useProcessSocialSignInResponse();

  const useStyles = makeStyles((theme) => ({
    root: {
      width: "100%",
      padding: signInModalOpen ? "0" : "10px",
      [theme.breakpoints.up("tablet")]: {
        padding: "30px 45px",
      },
    },
    signInOptionLabel: {
      ...theme.typography.button,
    },
    button: {
      // Special styling for sign in buttons
      ...theme.typography.button,
      color: "white",
      margin: "0.5rem 0",
      padding: "8px 18px",

      "&:hover": {
        background: theme.palette.primary.main,
        boxShadow: "none",
      },
    },
    buttonIcon: {
      width: "22px",
      marginRight: theme.spacing(1),
    },
    termsBox: {
      marginBottom: theme.spacing(2),
    },
    loadingOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
    },
  }));
  const classes = useStyles();

  const toggleSignInModal = () => {
    setSignInModalOpen(!signInModalOpen);
  };

  const toggleSignUpModal = () => {
    setSignUpModalOpen(!signUpModalOpen);
  };

  const isInIframe = checkIsInIframe();

  const signInWithGoogle = async () => {
    await signOutUser();
    if (isInIframe) {
      // Request parent to open popup
      requestParentToSignIn();
    } else {
      try {
        setLoading(true);
        const { firebaseToken, authUser } = await performGoogleSignIn();
        await processSocialSignInResponse(firebaseToken, authUser, isSignUp);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error(e);
      }
    }
  };

  const signInWithApple = async () => {
    await signOutUser();
    if (isInIframe) {
      // Request parent to open popup
      requestParentToSignIn();
    } else {
      try {
        setLoading(true);
        const { firebaseToken, authUser } = await performAppleSignIn();
        await processSocialSignInResponse(firebaseToken, authUser, isSignUp);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error(e);
      }
    }
  };

  const signInWithEmail = () => {
    if (props.logIn) {
      toggleSignInModal();
    } else {
      toggleSignUpModal();
    }
  };

  useEffect(() => {
    if (authUser && props.authCompleted) {
      props.authCompleted();
    }
  }, [authUser]);

  return (
    <div className={classes.root}>
      {loading && (
        <div className={classes.loadingOverlay}>
          <LoadingSpinner loading={loading} />
        </div>
      )}
      {signInModalOpen ? (
        <EmailAuth
          signIn
          open={signInModalOpen}
          onBack={toggleSignInModal}
          authUser={authUser}
          onNotifOpen={props.onNotifOpen}
          setLoading={setLoading}
        />
      ) : signUpModalOpen ? (
        <EmailAuth
          signUp
          open={signUpModalOpen}
          onBack={toggleSignUpModal}
          authUser={authUser}
          onNotifOpen={props.onNotifOpen}
          setLoading={setLoading}
        />
      ) : (
        <div>
          <Box width={1} mb={4}>
            <Typography
              variant={props.headingTextVariant || "h6"}
              color="textPrimary"
              align="center">
              {props.logIn ? props.loginText || "Log in" : "Sign up"}
            </Typography>
          </Box>
          <Grid container direction="column" justifyContent="center" wrap="nowrap">
            <Button
              variant="contained"
              size="small"
              onClick={signInWithEmail}
              className={classes.button}
              startIcon={<EmailIcon />}>
              {props.logIn ? "Login with email" : "Continue with email"}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={signInWithGoogle}
              className={classes.button}>
              <img
                src={googleIcon}
                className={classes.buttonIcon}
                alt="Google Icon"
                style={{ width: "18px" }}
              />
              {props.logIn ? "Login with Google" : "Continue with Google"}
            </Button>
            <Button variant="contained" onClick={signInWithApple} className={classes.button}>
              <AppleIcon className={classes.buttonIcon} />
              {props.logIn ? "Login with Apple" : "Continue with Apple"}
            </Button>
          </Grid>
        </div>
      )}
    </div>
  );
};

AuthCTA.propTypes = {
  authCompleted: PropTypes.func,
  onNotifOpen: PropTypes.func,
  headingTextVariant: PropTypes.string,
  logIn: PropTypes.bool,
  signUp: PropTypes.bool,
  loginText: PropTypes.string,
  subscribeOffers: PropTypes.bool,
};

export default AuthCTA;
